import { SignIn, useAuth } from '@clerk/clerk-react'
import { shadcn } from '@clerk/themes'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Login Page using Clerk's SignIn component with shadcn theme
 * 
 * Uses Clerk's shadcn theme which automatically adapts to your existing
 * shadcn/ui components and Tailwind CSS variables. Extended with Slate
 * Refinement design tokens for perfect integration.
 * 
 * The shadcn theme reads from your existing Tailwind CSS variables (defined in index.css),
 * so Clerk components will automatically match your shadcn/ui components.
 * 
 * Reference:
 * - https://clerk.com/docs/react/reference/components/authentication/sign-in
 * - https://clerk.com/docs/guides/customizing-clerk/appearance-prop/themes#shadcn-theme
 * - https://clerk.com/docs/guides/customizing-clerk/appearance-prop/overview
 * 
 * Installation:
 * npm install @clerk/themes
 */
export default function Login() {
  const { isSignedIn, isLoaded } = useAuth()
  const navigate = useNavigate()

  // Redirect to dashboard if already signed in
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [isLoaded, isSignedIn, navigate])

  // Show loading state while checking auth
  if (!isLoaded) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--background)' }}
      >
        <p className="text-text-secondary">Loading...</p>
      </div>
    )
  }

  // Don't render if already signed in (will redirect)
  if (isSignedIn) {
    return null
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding/Illustration */}
      <div 
        className="hidden lg:flex lg:w-1/2 items-center justify-center p-12"
        style={{
          background: 'linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-medium) 100%)',
          borderRight: '1.5px solid var(--accent-gold)',
        }}
      >
        <div className="max-w-md text-center space-y-6">
          <div className="text-6xl mb-4">🍽️</div>
          <h1 
            className="text-4xl font-bold"
            style={{
              fontFamily: 'var(--font-heading)',
              color: 'var(--cream-light)',
            }}
          >
            WaiterGPT
          </h1>
          <p 
            className="text-xl"
            style={{
              color: 'var(--cream-light)',
              opacity: 0.9,
            }}
          >
            AI-powered restaurant management
          </p>
          <p 
            className="text-sm mt-4"
            style={{
              color: 'var(--cream-light)',
              opacity: 0.8,
            }}
          >
            Sign in to access your admin dashboard and manage your restaurant operations.
          </p>
        </div>
      </div>

      {/* Right Side - Clerk SignIn Component */}
      <div 
        className="flex-1 flex items-center justify-center p-4 lg:p-12"
        style={{ backgroundColor: 'var(--background)' }}
      >
        <div className="w-full max-w-md">
          <SignIn
            routing="path"
            path="/admin/login"
            fallbackRedirectUrl="/admin/dashboard"
            forceRedirectUrl="/admin/dashboard"
            withSignUp={false}
            appearance={{
              // Use shadcn theme as base - automatically uses your Tailwind CSS variables
              // This makes Clerk components match your existing shadcn/ui components
              // The shadcn theme reads from: --primary, --foreground, --background, --card, etc.
              // Reference: https://clerk.com/docs/guides/customizing-clerk/appearance-prop/themes#shadcn-theme
              theme: shadcn,
              // Extend with additional Slate Refinement design tokens
              // Most styling comes from the theme, but we can override specific values if needed
              variables: {
                // These will override theme defaults if needed
                colorPrimary: 'hsl(var(--primary))', // #2c2d30 - Deep slate
                colorText: 'hsl(var(--foreground))', // #2c2d30 - Primary text
                colorTextSecondary: 'hsl(var(--muted-foreground))', // #5a5b5f - Secondary text
                colorBackground: 'hsl(var(--background))', // #f6f6f5 - Main background
                colorInputBackground: 'hsl(var(--card))', // #ffffff - Card/input background
                colorInputText: 'hsl(var(--foreground))', // #2c2d30 - Input text
                colorDanger: 'hsl(var(--destructive))', // #dc2626 - Error red
                colorBorder: 'hsl(var(--border))', // rgba(168, 146, 107, 0.2) - Bronze border
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-base)',
                borderRadius: 'var(--radius-md)', // 8px - matches design system
              },
              // Customize input elements to match our Input component exactly
              // Reference: https://clerk.com/docs/customization/elements/overview
              // Note: Focus and error states are handled by Clerk's shadcn theme using CSS variables
              elements: {
                // Input field styling - matches our Input component
                formFieldInput: {
                  backgroundColor: 'var(--card-bg)', // White background (#ffffff)
                  color: 'var(--text-primary)', // #2c2d30 - Primary text
                  borderColor: 'var(--border)', // Bronze border at 20% opacity
                  borderRadius: 'var(--radius-md)', // 8px border radius
                  padding: 'var(--space-3) var(--space-4)', // Matching our Input padding
                  fontSize: '0.9375rem', // Matching our Input font size (15px)
                  fontFamily: 'var(--font-body)',
                  transition: 'border-color var(--duration-normal) var(--ease-smooth)',
                  outline: 'none',
                },
                // Input label styling - matches our Label component
                formFieldLabel: {
                  color: 'var(--text-primary)', // #2c2d30 - Primary text
                  fontSize: '0.875rem', // 14px
                  fontWeight: 500,
                  fontFamily: 'var(--font-body)',
                  marginBottom: 'var(--space-2)',
                },
                // Input error message styling - matches our error text
                formFieldErrorText: {
                  color: 'var(--error)', // #dc2626 - Error red
                  fontSize: '0.8125rem', // 13px
                  marginTop: 'var(--space-1)',
                },
                // Primary button styling - matches our Button component (default variant)
                formButtonPrimary: {
                  backgroundColor: 'var(--primary)', // #2c2d30 - Deep slate
                  color: 'var(--cream-light)', // #f6f6f5 - Light cream text
                  borderColor: 'var(--accent-gold)', // #a8926b - Bronze border
                  borderRadius: 'var(--radius-md)', // 8px border radius
                  boxShadow: 'var(--shadow-button)', // Button shadow
                  minHeight: '44px', // Minimum touch target
                  fontFamily: 'var(--font-body)',
                  fontWeight: 500,
                  transition: 'all var(--duration-normal) var(--ease-smooth)',
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  )
}
